import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ModalBackdrop from './components/ModalBackdrop';
import ModalHeader from './components/ModalHeader';
import RoleCard from './components/RoleCard';
import ModalFooter from './components/ModalFooter';

const RoleSelectionModal = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleContinue = async () => {
    if (!selectedRole) return;
    
    setIsLoading(true);
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      if (selectedRole === 'job-seeker') {
        navigate('/job-seeker-registration');
      } else if (selectedRole === 'employer') {
        navigate('/employer-registration');
      }
      setIsLoading(false);
    }, 800);
  };

  const handleClose = () => {
    navigate('/homepage');
  };

  const roles = [
    {
      id: 'job-seeker',
      title: 'Job Seeker',
      description: 'Create an anonymous profile and get discovered by top employers without bias',
      features: [
        'Free anonymous profile creation',
        'Skills-based matching system',
        'Direct employer contact after unlock',
        'Complete privacy protection',
        'Anti-bias hiring experience',
        'Portfolio and certification showcase'
      ],
      icon: 'User',
      color: 'primary',
      buttonText: 'Start as Job Seeker'
    },
    {
      id: 'employer',
      title: 'Employer',
      description: 'Search and unlock talented candidates for your open positions efficiently',
      features: [
        'Advanced candidate search filters',
        'Pay-per-unlock pricing model (149 NOK)',
        'Direct messaging with candidates',
        'Team collaboration tools',
        'Hiring analytics and insights',
        'Saved searches with alerts'
      ],
      icon: 'Building2',
      color: 'accent',
      buttonText: 'Start as Employer'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Choose Your Role - Workfolio</title>
        <meta name="description" content="Select whether you're a job seeker looking for opportunities or an employer searching for talent on Workfolio's anonymous recruitment platform." />
        <meta property="og:title" content="Choose Your Role - Workfolio" />
        <meta property="og:description" content="Join Workfolio as a job seeker or employer and experience bias-free recruitment." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <ModalBackdrop onClose={handleClose}>
        <ModalHeader onClose={handleClose} />
        
        <div className="p-8 pt-6">
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {roles?.map((role) => (
              <RoleCard
                key={role?.id}
                role={role?.id}
                isSelected={selectedRole === role?.id}
                onSelect={handleRoleSelect}
                title={role?.title}
                description={role?.description}
                features={role?.features}
                icon={role?.icon}
                color={role?.color}
                buttonText={role?.buttonText}
              />
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-success"></div>
                <span>GDPR Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-success"></div>
                <span>Norwegian Business</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-success"></div>
                <span>Secure & Anonymous</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-success"></div>
                <span>Fair Hiring Practices</span>
              </div>
            </div>
          </div>
        </div>

        <ModalFooter 
          selectedRole={selectedRole}
          onContinue={handleContinue}
          onClose={handleClose}
          isLoading={isLoading}
        />
      </ModalBackdrop>
    </>
  );
};

export default RoleSelectionModal;